import { useState, useRef, useCallback } from 'react';
import Modal from './Modal';
import Icon  from './Icon';
import { Storage } from '../storage';

// ─── GEDCOM IMPORT MODAL ──────────────────────────────────────────────────────
//
//  Props:
//    open           {boolean}
//    onClose        {function}
//    timelineId     {string}
//    isTreeLayout   {boolean}  warn if false
//    onImported     {function(result)}
//

const GEDCOMImportModal = ({ open, onClose, timelineId, isTreeLayout, onImported }) => {
  const [file,      setFile]      = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');
  const fileInputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); setError(''); };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handleFileAccepted = (f) => {
    setError('');
    setResult(null);
    if (!/\.(ged|gedcom)$/i.test(f.name)) {
      setError('Only .ged and .gedcom files are supported.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('File exceeds the 5 MB size limit.');
      return;
    }
    setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileAccepted(f);
  }, []);

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true);  };
  const handleDragLeave = ()  => setDragOver(false);
  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) handleFileAccepted(f);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!file || importing) return;
    setImporting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await Storage.importEventsFromGEDCOM(timelineId, fd);
      setResult(res);
      onImported(res);
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Import Family Tree (GEDCOM)" width={560}>

      {/* Tree orientation advisory */}
      {!isTreeLayout && (
        <div className="imp-warn" style={{ marginBottom: 16 }}>
          <Icon name="warning" size={14} />
          <span>
            This timeline uses the <strong>{/* orientation name shown by caller */}</strong> layout.
            GEDCOM imports work best with the <strong>Tree</strong> layout — consider switching
            before importing.
          </span>
        </div>
      )}

      <div className="imp-hint">
        <Icon name="info" size={14} />
        <span>
          Each person with a birth year becomes an event. Parent–child relationships
          are preserved via family records.
        </span>
      </div>

      {/* Drop zone */}
      {!result && (
        <div
          className={`imp-dropzone${dragOver ? ' imp-dropzone--over' : ''}${file ? ' imp-dropzone--has-file' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !file && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Drop GEDCOM file here or click to browse"
          onKeyDown={(e) => e.key === 'Enter' && !file && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".ged,.gedcom"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
          {file ? (
            <div className="imp-file-selected">
              <Icon name="document" size={20} />
              <span className="imp-filename">{file.name}</span>
              <button
                className="imp-remove-file"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                aria-label="Remove file"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ) : (
            <>
              <Icon name="users" size={28} />
              <p className="imp-drop-label">Drop your GEDCOM file here or <span className="imp-link">browse</span></p>
              <p className="imp-drop-sub">Max 5 MB · .ged or .gedcom</p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="imp-error">
          <Icon name="warning" size={14} />
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="imp-result">
          <div className="imp-result-stats">
            <div className="imp-stat imp-stat--ok">
              <span className="imp-stat-num">{result.imported}</span>
              <span className="imp-stat-lbl">imported</span>
            </div>
            {result.skipped > 0 && (
              <div className="imp-stat imp-stat--warn">
                <span className="imp-stat-num">{result.skipped}</span>
                <span className="imp-stat-lbl">skipped</span>
              </div>
            )}
          </div>

          {result.errors?.length > 0 && (
            <div className="imp-msg-list">
              <p className="imp-msg-list-title">Skipped (no birth date)</p>
              {result.errors.slice(0, 8).map((e, i) => (
                <p key={i} className="imp-msg-item imp-msg-item--warn">{e.message}</p>
              ))}
              {result.errors.length > 8 && (
                <p className="imp-msg-item imp-msg-item--warn">…and {result.errors.length - 8} more</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="imp-actions">
        {result ? (
          <>
            <button className="btn-secondary" onClick={reset}>Import Another</button>
            <button className="btn-primary" onClick={handleClose}>Done</button>
          </>
        ) : (
          <>
            <button className="btn-secondary" onClick={handleClose} disabled={importing}>Cancel</button>
            <button className="btn-primary" onClick={handleImport} disabled={!file || importing}>
              {importing ? 'Importing…' : 'Import Family Tree'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default GEDCOMImportModal;
